/* eslint-disable no-bitwise */

import { ColumnsType } from 'antd/es/table/interface';
import { Dayjs } from 'dayjs';
import { getQueryString } from '@sonar-software/sonar-http';
import { isArray } from 'lodash';
import { LookAheadFilterOption } from '@ui/table-modifier/filters/LookAheadFilter';
import { ManagedPackageValues } from '@ui/table-modifier/filters/ManagedPackage';
import { PopulatedValues } from '@ui/table-modifier/filters/Populated';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { translateApiResultsToDataDictionaryColumns } from '@dictionary/DataDictionaryTableColumns';

import {
	buildDynamicFilterLookupFromString,
	buildStringEnumValidator,
	getDateRangeValuesFromDynamicFilterLookup,
	getMultiSelectValuesFromDynamicFilterLookup,
	getOrderingFromDynamicOrderString,
	getRangeValuesFromDynamicFilterLookup,
	getStatusValueFromDynamicFilterLookup,
	getValuesFromDynamicFilterLookup,
	makeDynamicFilterBetweenString,
	makeDynamicFilterRangeString,
	makeDynamicFilterString,
	objectKeys,
	parseQueryString
} from '@sonar-software/sonar-ts-lib';
import {
	ComplianceCategory,
	DataDictionaryEntry,
	DataDictionarySchema,
	FeatureFlag,
	SchemaFormat
} from '@sonar-software/sonar-core';
import { COPY_SPECIFIC, FORMATS } from '@sonar-software/sonar-ts-constants';
import {
	DynamicFilter,
	DynamicFilterEntry,
	DynamicQueryOperator,
	SonarV3SortDirection,
	SortByOption
} from '@sonar-software/sonar-ts-types';
import { TableFilter, TableFilterQueryParams } from '@ui/table-modifier';

const multiselectEnums = [
	'AccessLevel',
	'AccessObjectSource',
	'AccessType',
	'DataSensitivityLevelId',
	'FieldUsageId',
	'Type'
];

export function aggregateComplianceCategories(categories: ComplianceCategory[]): ComplianceCategory | undefined {
	if (categories.includes(ComplianceCategory.None)) {
		return ComplianceCategory.None;
	} else if (categories.includes(ComplianceCategory.Any)) {
		return ComplianceCategory.Any;
	}

	let aggregatedValue = 0 as ComplianceCategory;
	categories.forEach((category) => {
		aggregatedValue |= category as unknown as number;
	});

	return aggregatedValue;
}

export function splitComplianceCategories(aggregateCategory: number): Array<ComplianceCategory> {
	if (aggregateCategory === ComplianceCategory.None) {
		return [ ComplianceCategory.None ];
	} else if (aggregateCategory === ComplianceCategory.Any) {
		return [ ComplianceCategory.Any ];
	}

	const results: Array<ComplianceCategory> = [];

	for (const category in ComplianceCategory) {
		const categoryNumber = Number(category);

		if ((aggregateCategory & categoryNumber) !== 0 &&
			categoryNumber !== ComplianceCategory.None &&
			categoryNumber !== ComplianceCategory.Any) {
			results.push(categoryNumber);
		}
	}

	return results;
}

export function convertArrayToFilterString(value: object, filterKey: string): string {
	const valueArray = value as Array<string | number>;
	const hasNoSelectionValue = valueArray.includes(-1) || valueArray.includes('null');

	if (hasNoSelectionValue && valueArray.length === 1) {
		return makeDynamicFilterString(filterKey, '', DynamicQueryOperator.IsNull);
	}

	const dynamicQueryOperator = hasNoSelectionValue ? DynamicQueryOperator.Inn : DynamicQueryOperator.In;
	const cleanedValues = valueArray.filter((rawValue) => rawValue !== -1 && rawValue !== 'null');

	return makeDynamicFilterString(filterKey, cleanedValues, dynamicQueryOperator);
}

export function getFilterId(filterId: string): string {
	return `data-dictionary-${filterId}-filter`;
}

export function getAppliedFilters(filters: string): string {
	return filters.substring('Filter = '.length, filters.indexOf('&'));
}

export function getAppliedOrdering(ordering: string): string {
	return ordering.substring(ordering.indexOf('&Ordering = ') + '&Ordering = '.length);
}

export function translateFilterDisplayNamesToQueryParams(
	filterParams: DynamicFilter
): string {
	const filterKeyArray = Object.keys(filterParams);

	if (filterKeyArray.length === 0) {
		return '';
	}

	const displayNames = filterKeyArray.reduce((acc, filterKey) => {
		const { value } = filterParams[ filterKey ];

		if (!value || !isArray(value)) {
			return { ...acc };
		}

		return value?.reduce((display, option: LookAheadFilterOption) => {
			if (!option || option.value === 'null') {
				return { ...display };
			}

			return {
				...display,
				[ `${filterKey}-${option.value}` ]: option.label
			};
		}, acc);
	}, {});

	return getQueryString(displayNames);
}

export function translateFilterQueryParamsToDynamicQueryLanguage(filterParams: DynamicFilter): string {
	const filterKeyArray = Object.keys(filterParams);

	if (filterKeyArray.length === 0) {
		return '';
	}

	const filterArray = filterKeyArray.map((filterKey) => {
		const { format, value } = filterParams[ filterKey ];

		if (value === null || value === undefined) {
			return '';
		}

		switch (format) {
			case SchemaFormat.BooleanToggle:
			case SchemaFormat.BooleanToggleInverse:
			case SchemaFormat.Checkmark:
			case SchemaFormat.Enum:
				if (multiselectEnums.includes(filterKey)) {
					return convertArrayToFilterString(value, filterKey);
				}

				if (filterKey === 'ComplianceCategory') {
					const operator = value === ComplianceCategory.None ?
						DynamicQueryOperator.BitwiseOrWithNull :
						DynamicQueryOperator.BitwiseOr;

					return makeDynamicFilterString(filterKey, Number(value), operator);
				}

				return makeDynamicFilterString(filterKey, value as string | number, DynamicQueryOperator.Equals);
			case SchemaFormat.PopulationPercentage:
				return makeDynamicFilterBetweenString(filterKey, value as [number, number, boolean]);
			case SchemaFormat.ReferenceCount:
				return makeDynamicFilterRangeString(filterKey, value as [number, number]);
			case SchemaFormat.DateTime:
				const formattedValues = (value as [Dayjs | null, Dayjs | null]).map((dateMoment) =>
					dateMoment?.format(FORMATS.SHORT_DATE));

				return makeDynamicFilterRangeString(filterKey, formattedValues);
			case SchemaFormat.Object:
			case SchemaFormat.Owner:
			case SchemaFormat.PageLayouts:
			case SchemaFormat.PermissionSources:
			case SchemaFormat.Profiles:
			case SchemaFormat.SalesforceUser:
			case SchemaFormat.Stakeholder:
			case SchemaFormat.Tags:
			case SchemaFormat.Apps:
				return convertArrayToFilterString(value, filterKey);
			case SchemaFormat.Primary:
			case SchemaFormat.PrimaryInternal:
			case SchemaFormat.Text:
				return makeDynamicFilterString(filterKey, value as string, DynamicQueryOperator.Like);
			case SchemaFormat.ManagedPackage:
				const selected = value as string;

				if (selected === ManagedPackageValues.isNullValue) {
					return makeDynamicFilterString(filterKey, '', DynamicQueryOperator.IsNull);
				}

				return makeDynamicFilterString(filterKey, '', DynamicQueryOperator.NotNull);
			case SchemaFormat.Description:
			case SchemaFormat.HelpText:
				const selectedValue = value as string;

				if (selectedValue === PopulatedValues.isNullValue) {
					return makeDynamicFilterString(filterKey, '', DynamicQueryOperator.IsNull);
				}

				return makeDynamicFilterString(filterKey, '', DynamicQueryOperator.NotNull);
			case SchemaFormat.LookAheadSalesforceUser:
				return convertArrayToFilterString(
					(value as Array<LookAheadFilterOption>)?.map((item) => item.value),
					filterKey
				);
			default:
				return '';
		}
	});

	const filters = filterArray?.filter((filter) => filter !== '').join(' ');

	return filters;
}

export function translateOrderQueryParamsToDynamicQueryLanguage(sortParams: SortByFilterQueryParams): string {
	return `${sortParams.sortBy}~${sortParams.sortDirection === SonarV3SortDirection.Ascending ? 'a' : 'd'}`;
}

const InverseList = [
	SchemaFormat.BooleanToggleInverse
];

export function translateFilterAndOrderingToQueryParams(
	filters: Array<TableFilter>,
	sortOptions: Array<SortByOption>,
	filterString?: string,
	orderingString?: string,
	displayNameString?: string
): TableFilterQueryParams {
	const record: TableFilterQueryParams = {};

	const filterLookup = buildDynamicFilterLookupFromString(filterString);
	const displayNameLookup = parseQueryString(displayNameString) as Record<string, string>;

	let inverseFilterCount = 0;

	filters?.forEach((filter) => {
		const { filterId, filterFormat } = filter;

		if (!filterString) {
			return;
		}

		const filterObject: DynamicFilterEntry = {
			format: filterFormat,
			value: undefined
		};

		switch (filterFormat) {
			case SchemaFormat.BooleanToggleInverse:
				filterObject.value = getValuesFromDynamicFilterLookup(filterId, filterLookup)[ 0 ]?.value || null;
				if (filterObject.value) {
					inverseFilterCount += 1;
				}
				break;
			case SchemaFormat.BooleanToggle:
			case SchemaFormat.Checkmark:
				filterObject.value = getValuesFromDynamicFilterLookup(filterId, filterLookup)[ 0 ]?.value || null;
				break;
			case SchemaFormat.ReferenceCount:
				filterObject.value = getRangeValuesFromDynamicFilterLookup(filterId, filterLookup);
				break;
			case SchemaFormat.DateTime:
				filterObject.value = getDateRangeValuesFromDynamicFilterLookup(filterId, filterLookup);
				break;
			case SchemaFormat.PopulationPercentage:
				filterObject.value = getMultiSelectValuesFromDynamicFilterLookup(filterId, filterLookup);
				break;
			case SchemaFormat.Enum:
				filterObject.value = multiselectEnums.includes(filterId) ?
					getMultiSelectValuesFromDynamicFilterLookup(filterId, filterLookup, false) :
					getStatusValueFromDynamicFilterLookup(filterId, filterLookup);
				break;
			case SchemaFormat.SalesforceUser:
			case SchemaFormat.Object:
				filterObject.value = getMultiSelectValuesFromDynamicFilterLookup(filterId, filterLookup, true);
				break;
			case SchemaFormat.Owner:
			case SchemaFormat.PageLayouts:
			case SchemaFormat.PermissionSources:
			case SchemaFormat.Profiles:
			case SchemaFormat.Stakeholder:
			case SchemaFormat.Tags:
			case SchemaFormat.Apps:
				filterObject.value = getMultiSelectValuesFromDynamicFilterLookup(filterId, filterLookup, false);
				break;
			case SchemaFormat.Primary:
			case SchemaFormat.PrimaryInternal:
			case SchemaFormat.Text:
				filterObject.value = getValuesFromDynamicFilterLookup(filterId, filterLookup)[ 0 ]?.value ?? '';
				break;
			case SchemaFormat.LookAheadSalesforceUser:
				const lookAheadids = getMultiSelectValuesFromDynamicFilterLookup(filterId, filterLookup, true);
				const values: Array<LookAheadFilterOption> = [];

				lookAheadids.forEach((lookAheadid: string) => {
					values.push({
						label: displayNameLookup[ `${filterId}-${lookAheadid}` ] ?? '',
						value: lookAheadid ?? ''
					});
				});

				filterObject.value = values;

				break;
			case SchemaFormat.ManagedPackage:
				const managedPackageOperator = getValuesFromDynamicFilterLookup(filterId, filterLookup)[ 0 ]?.operator;

				if (managedPackageOperator === DynamicQueryOperator.IsNull) {
					filterObject.value = ManagedPackageValues.isNullValue;
				} else if (managedPackageOperator === DynamicQueryOperator.NotNull) {
					filterObject.value = ManagedPackageValues.notNullValue;
				}

				break;
			case SchemaFormat.Description:
			case SchemaFormat.HelpText:
				const operator = getValuesFromDynamicFilterLookup(filterId, filterLookup)[ 0 ]?.operator;

				if (operator === DynamicQueryOperator.IsNull) {
					filterObject.value = PopulatedValues.isNullValue;
				} else if (operator === DynamicQueryOperator.NotNull) {
					filterObject.value = PopulatedValues.notNullValue;
				}

				break;
			default:
				return;
		}

		record[ filterId ] = filterObject;
	});

	record.filterCount =
		objectKeys(filterLookup)
			.filter((x) => Boolean(filters.find((y) => y.filterId === x))).length +
		filters.filter((x) => InverseList.includes(x.filterFormat)).length -
		(2 * inverseFilterCount);

	if (orderingString) {
		const sortTarget = getOrderingFromDynamicOrderString(orderingString, sortOptions);

		if (sortTarget) {
			record.sortBy = sortTarget.key;
			record.sortDirection = sortTarget.direction;
			record.sortDefault = sortTarget;
		}
	}

	return record;
}

export function getColumnsString(columnIds: string[], visibleColumns?: Record<string, boolean>) {
	return columnIds.map((id) => `${!visibleColumns || visibleColumns[ id ] ? 1 : 0}${id}`).join('');
}

export function parseColumnString(str?: string): { ids: Array<string>; isVisible: Record<string, boolean> } {
	const accumlator = {
		ids: [],
		isVisible: {} as Record<string, boolean>
	};

	if (!str) {
		return accumlator;
	}

	return str.split(/(?=1|0)/gu).reduce((result, columnStr) => {
		const id = columnStr.slice(1);
		const visible = Number(columnStr[ 0 ]) === 1;

		return {
			ids: [ ...result.ids, id ],
			isVisible: {
				...result.isVisible,
				[ id ]: visible
			}
		};
	}, accumlator);
}

const isSchemaFormatValid = buildStringEnumValidator(SchemaFormat);

export type DataDictionaryMetadata = {
	columns: Array<{ id: string; name: string }>;
	filters: Array<TableFilter>;
	schema: ColumnsType<DataDictionaryEntry>;
	sortOptions: Array<SortByOption>;
};

export function parseMetadataFromResponse(
	schema: DataDictionarySchema[] | null | undefined,
	featureFlagCheck: (flag: FeatureFlag) => boolean,
	currentColumns?: string,
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement
): DataDictionaryMetadata {
	if (!schema) {
		return {
			columns: [],
			filters: [],
			schema: [],
			sortOptions: []
		};
	}

	const columns = schema.reduce((result, column) => {
		const format = column.format.includes(SchemaFormat.Enum) ? SchemaFormat.Enum : column.format;

		if (format === SchemaFormat.None || format.includes('Filter') || !isSchemaFormatValid(format)) {
			return result;
		}

		return [
			...result,
			{
				id: column.columnId,
				name: column.columnName
			}
		];
	}, []);

	const { ids: columnOrder, isVisible } = parseColumnString(
		currentColumns || getColumnsString(columns.map((column) => column.id))
	);

	const arrangedAndFilteredSchema = columnOrder.reduce((arranged, id) => {
		if (isVisible[ id ]) {
			const index = schema.findIndex((item) => item.columnId === id);
			if (index >= 0) {
				arranged.push(schema[ index ]);
			}
		}

		return arranged;
	}, [] as DataDictionarySchema[]);

	const columnsFromSchema = translateApiResultsToDataDictionaryColumns(
		arrangedAndFilteredSchema ?? [],
		getPopupContainer
	);

	const filterableSchema = schema.filter((schemaObject) => schemaObject.filterable);
	const sortableSchema = schema.filter((schemaObject) => schemaObject.sortable);

	const indexOfFirstApplication = filterableSchema.findIndex((x) => x.format === SchemaFormat.Apps);
	const uniqueFilterableSchema = filterableSchema.filter(
		(x, index) =>
			x.format !== SchemaFormat.Apps || (x.format === SchemaFormat.Apps && index !== indexOfFirstApplication)
	);

	const filtersFromSchema: Array<TableFilter> = uniqueFilterableSchema.map((schemaObject) => {
		const format = schemaObject.format.includes(SchemaFormat.Enum) ?
			SchemaFormat.Enum :
			(schemaObject.format as SchemaFormat);

		if (format === SchemaFormat.Apps) {
			return {
				filterFormat: format,
				filterId: 'IntegratedAppIds',
				filterLabel: COPY_SPECIFIC.MONITORED_APPLICATIONS
			};
		}

		return {
			filterFormat: format,
			filterId: schemaObject.columnId,
			filterLabel: schemaObject.columnName,
			filterMetadata: schemaObject.metadata
		};
	});

	const defaultAscending = [ SchemaFormat.Text, SchemaFormat.Primary, SchemaFormat.PrimaryInternal ];

	const sortOptionsFromSchema: Array<SortByOption> = sortableSchema.map((schemaObject) => ({
		direction: defaultAscending.includes(schemaObject.format as SchemaFormat) ?
			SonarV3SortDirection.Ascending :
			SonarV3SortDirection.Descending,
		key: schemaObject.columnId,
		label: schemaObject.columnName
	}));

	return {
		columns: columns,
		filters: filtersFromSchema,
		schema: columnsFromSchema,
		sortOptions: sortOptionsFromSchema
	};
}
